import { useEffect, useRef, useId } from "react";
import { Box, Typography } from "@mui/material";

const JSXGRAPH_JS = "https://cdn.jsdelivr.net/npm/jsxgraph/distrib/jsxgraphcore.js";
const JSXGRAPH_CSS = "https://cdn.jsdelivr.net/npm/jsxgraph/distrib/jsxgraph.css";

let jsxgraphLoadPromise = null;

function loadJsxGraph() {
  if (window.JXG) return Promise.resolve();
  if (jsxgraphLoadPromise) return jsxgraphLoadPromise;
  jsxgraphLoadPromise = new Promise((resolve, reject) => {
    if (!document.querySelector('link[data-jsxgraph="1"]')) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = JSXGRAPH_CSS;
      link.setAttribute("data-jsxgraph", "1");
      document.head.appendChild(link);
    }
    const script = document.createElement("script");
    script.src = JSXGRAPH_JS;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load JSXGraph"));
    document.head.appendChild(script);
  });
  return jsxgraphLoadPromise;
}

export default function GeometryMarkSegment({ config, value, onChange }) {
  const boardId = `jxg-${useId().replace(/:/g, "")}`;
  const boardRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const valueRef = useRef(value);
  onChangeRef.current = onChange;
  valueRef.current = value;

  useEffect(() => {
    if (!config || config.kind !== "segment_mark") return undefined;

    let cancelled = false;

    loadJsxGraph()
      .then(() => {
        if (cancelled || !window.JXG) return;

        const A = config.A || [0, 0];
        const B = config.B || [10, 0];
        const ppc = config.pixels_per_cm || 40;
        const target = config.target_from_A_cm ?? 2.5;

        const board = window.JXG.JSXGraph.initBoard(boardId, {
          boundingbox: [-1, 2, Math.max(B[0], 10) + 1, -2],
          axis: false,
          showCopyright: false,
          keepaspectratio: true,
        });
        boardRef.current = board;

        const pA = board.create("point", A, { name: "A", fixed: true, size: 3 });
        const pB = board.create("point", B, { name: "B", fixed: true, size: 3 });
        const seg = board.create("segment", [pA, pB], { strokeWidth: 2 });

        const len = Math.hypot(B[0] - A[0], B[1] - A[1]) || 1;
        const initial = valueRef.current;
        const tInit = initial?.geometry?.t ?? target / len;
        const pP = board.create(
          "point",
          [A[0] + tInit * (B[0] - A[0]), A[1] + tInit * (B[1] - A[1])],
          { name: "P", size: 4, slideObject: seg }
        );

        const emitGeometry = () => {
          const ax = A[0];
          const ay = A[1];
          const bx = B[0];
          const by = B[1];
          const segLen = Math.hypot(bx - ax, by - ay) || 1;
          const t = Math.max(
            0,
            Math.min(1, ((pP.X() - ax) * (bx - ax) + (pP.Y() - ay) * (by - ay)) / (segLen * segLen))
          );
          const v = valueRef.current;
          onChangeRef.current({
            text: typeof v === "object" && v?.text != null ? v.text : "",
            geometry: { t, distance_from_A_cm: t * segLen, pixels_per_cm: ppc },
          });
        };

        pP.on("drag", emitGeometry);

        board.unsuspendUpdate();
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      if (boardRef.current) {
        try {
          window.JXG.JSXGraph.freeBoard(boardRef.current);
        } catch {
          /* ignore */
        }
        boardRef.current = null;
      }
    };
  }, [boardId, config]);

  if (!config || config.kind !== "segment_mark") return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75, fontWeight: 600 }}>
        Drag point P along the line (then type your fraction or measurement below if required).
      </Typography>
      <Box
        id={boardId}
        className="jxgbox"
        sx={{
          width: "100%",
          maxWidth: 520,
          height: 160,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          bgcolor: "#fff",
        }}
      />
    </Box>
  );
}
